import { Navigate, Route, useRoutes, useLocation, useNavigate } from 'react-router-dom';
import { JSX } from 'react';
import { useAuth } from './context/auth/AuthContext';
import { useEffect, useMemo, useState } from 'react';
import DashboardLayout from './layouts/dashboard';
import LogoOnlyLayout from './layouts/LogoOnlyLayout';
import { LoadingScreen } from './components';
import { resolveRouteAccess, homeRoute } from './config/routeAccess';
import { canAccessRouteSection } from './config/permissions';
import { getRouteRequirement } from './config/routePermissions';
import { PermissionKey } from './types/permissions.types';

// Auth Pages
import Login from './pages/Login';
import NotFound from './pages/Page404';
import ForgotPassword from './pages/ForgotPassword';
import CreateNewPassword from './pages/CreateNewPassword';
// Teacher Pages
import TeacherDashboard from './pages/Teacher/Dashboard';
import { AddStudent, Students, EditStudent, ViewStudent } from './pages/Teacher/student';
import { LoanRequests, ViewLoan } from './pages/Teacher/loanrequests';
import { StreetCents, ViewStreetCent } from './pages/Teacher/streetcents';
import { Badges, AddNewBadges, EditBadge } from './pages/Teacher/badges';
import {
  FinancialSimulations,
  // AssignNewChellenge,
  ViewFinancialSimulations as TeacherViewFinancialSimulations,
} from './pages/Teacher/financialsimulations';
import { ViewFinancialSimulations } from './pages/Admin/financialsimulations';

import { StreetCred } from './pages/Teacher/streetcred';
import { Classes, AddNewClass, AddStundets } from './pages/Teacher/classes';
import { EditProfile, ChangePassword } from './pages/Teacher/settings';
import TransactionManagement from './pages/Teacher/transactionmanagement';

// Admin Pages
import { ManageTransactionTypes, AddTransactionType, EditTransactionType } from './pages/Admin/managetransactiontypes';
import {
  AdminFinancialSimulations,
  AddScenario,
  EditScenario,
  AssignNewChellenge,
} from './pages/Admin/financialsimulations';
import { StreetCredAdmin, AddBehaviorType, EditBehaviorType, AddBehaviour } from './pages/Admin/streetcred';
import { Teachers, AddTeacher, EditTeacher, ViewTeacher, EnrolledStudents } from './pages/Admin/teachers';

// Student Pages
import StudentDashboard from './pages/Student/dashboard';
import { StudentLoanRequests, NewLoanRequest } from './pages/Student/loanrequests';
import { StudentFinancialSimulations, StudentViewFinancialSimulations } from './pages/Student/financialsimulations';
import FinancialTools from './pages/Student/financialtools';
import Bills from './pages/Student/bills';
import { Transactions } from './pages/Admin/transactions/Transactions';
import TransactionStatusTrackerPage from './pages/Admin/transactions/TransactionStatusTracker';
import ManageStreetCredsAdmin from './pages/Admin/streetcred/StreetCreds';
import {
  PayrollHub,
  PayrollImportWizard,
  PayrollImportDetail,
  RosterImport,
  TaxDayExport,
} from './pages/Admin/payroll';
import StudentPaychecks from './pages/Student/paychecks/StudentPaychecks';
import CohortsHub from './pages/Admin/cohorts/CohortsHub';
import CohortDetail from './pages/Admin/cohorts/CohortDetail';
import Roles from './pages/Admin/roles/Roles';
import RoleDetail from './pages/Admin/roles/RoleDetail';
// import EnrolledStudents from './pages/Teacher/classes/enrolledstudents';

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Top-level section this route belongs to, for example '/admin'. */
  section?: string;
  /** Permission required in addition to the section check. */
  requiredPermission?: PermissionKey | string;
  /** Resource the route reads. Any scope above NONE passes. */
  requiredViewResource?: string;
}

/** Gates a route. All policy lives in `resolveRouteAccess`. */
function ProtectedRoute({
  children,
  section,
  requiredPermission,
  requiredViewResource,
}: ProtectedRouteProps) {
  const { userRole, isAuthenticated, isLoading, error } = useAuth();
  const navigate = useNavigate();

  const decision = resolveRouteAccess({
    isLoading,
    isAuthenticated,
    role: userRole,
    section,
    requiredPermission,
    requiredViewResource,
    sessionExpired: error?.message?.includes('expired'),
  });
  const redirectTo = decision.status === 'redirect' ? decision.to : null;

  useEffect(() => {
    if (!redirectTo) return;
    navigate(redirectTo, { replace: true });
  }, [redirectTo, navigate]);

  if (decision.status !== 'allow') {
    return <LoadingScreen message="Checking access..." />;
  }

  return <>{children}</>;
}

// Auth Guard Component
/** Sends signed-in users off the public pages to their own dashboard. */
function AuthGuard({ children }: { children: React.ReactNode }) {
  const { userRole, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  const target = !isLoading && isAuthenticated && userRole ? homeRoute(userRole) : null;

  useEffect(() => {
    if (!target) return;
    navigate(target, { replace: true });
  }, [target, navigate]);

  if (isLoading) {
    return <LoadingScreen message="Loading..." />;
  }

  return <>{children}</>;
}

/** Chooses where a visitor to `/` or `/dashboard` belongs. */
function RootRedirect({
  isAuthenticated,
  userRole,
  replace = false,
}: {
  isAuthenticated: boolean;
  userRole?: string;
  replace?: boolean;
}) {
  if (!isAuthenticated) return <Navigate to="/login" replace={replace} />;
  if (!userRole) return <LoadingScreen message="Loading user data..." />;
  return <Navigate to={homeRoute(userRole)} replace={replace} />;
}

interface AppRoute {
  path: string;
  element: JSX.Element;
}

/**
 * Wraps each route in a guard using its entry in ROUTE_REQUIREMENTS. Routes
 * with no entry pass through unguarded.
 */
function withGuards(section: string, routes: AppRoute[]) {
  return routes.map(({ path, element }) => {
    const { permission, viewResource } = getRouteRequirement(section, path);
    if (!permission && !viewResource) return { path, element };
    return {
      path,
      element: (
        <ProtectedRoute requiredPermission={permission} requiredViewResource={viewResource}>
          {element}
        </ProtectedRoute>
      ),
    };
  });
}

// Route Configurations
const teacherRoutes: AppRoute[] = [
  { path: 'dashboard', element: <TeacherDashboard /> },
  { path: 'transaction-management', element: <TransactionManagement /> },
  { path: 'students', element: <Students /> },

  { path: 'add-student', element: <AddStudent /> },
  { path: 'edit-student/:studentId', element: <EditStudent /> },
  { path: 'view-student/:studentId', element: <ViewStudent /> },
  { path: 'loan-requests', element: <LoanRequests /> },
  { path: 'view-loan/:loanId', element: <ViewLoan /> },
  { path: 'street-cents', element: <StreetCents /> },
  { path: 'street-cents/:id', element: <ViewStreetCent /> },
  { path: 'view-transaction/:id', element: <ViewStreetCent /> },
  { path: 'badges', element: <Badges /> },
  { path: 'add-new-badge', element: <AddNewBadges /> },
  { path: 'edit-badge/:badgeId', element: <EditBadge /> },
  { path: 'enrolled-students/:id', element: <EnrolledStudents /> },

  // { path: 'financial-simulations', element : <FinancialSimulations /> },
  { path: 'financial-simulations', element: <AdminFinancialSimulations /> },
  { path: 'view-financial-simulations/:id', element: <ViewFinancialSimulations /> },
  { path: 'add-scenario', element: <AddScenario /> },
  { path: 'edit-scenario/:scenarioId', element: <EditScenario /> },
  { path: 'street-cred', element: <StreetCred /> },
  { path: 'add-student-behaviour', element: <AddBehaviour /> },
  { path: 'add-student-behaviour/:id', element: <AddBehaviour /> },
  { path: 'edit-student-behavior/:id', element: <AddBehaviour /> },
  { path: 'add-behavior-type', element: <AddBehaviorType /> },
  { path: 'edit-behaviour-type/:behaviourId', element: <EditBehaviorType /> },
  { path: 'assign-new-chellenge', element: <AssignNewChellenge /> },
  { path: 'assign-new-chellenge/:id', element: <AssignNewChellenge /> },
  { path: 'classes', element: <Classes /> },
  { path: 'add-new-class', element: <AddNewClass /> },
  { path: 'add-students/:id', element: <AddStundets /> },
  { path: 'cohorts', element: <CohortsHub /> },
  { path: 'cohorts/:cohortId', element: <CohortDetail /> },
  { path: 'edit-profile', element: <EditProfile /> },
  { path: 'change-password', element: <ChangePassword /> },
];

const adminRoutes: AppRoute[] = [
  { path: 'dashboard', element: <TeacherDashboard /> },
  { path: 'teachers', element: <Teachers /> },
  { path: 'add-teacher', element: <AddTeacher /> },
  { path: 'edit-teacher/:teacherId', element: <EditTeacher /> },
  { path: 'view-teacher/:teacherId', element: <ViewTeacher /> },
  { path: 'manage-transaction-types', element: <ManageTransactionTypes /> },
  { path: 'add-transaction-type', element: <AddTransactionType /> },
  { path: 'edit-transaction-type/:transactionTypeId', element: <EditTransactionType /> },
  { path: 'transaction-status-tracker', element: <TransactionStatusTrackerPage /> },
  { path: 'new-loan-request', element: <NewLoanRequest /> },

  // { path: 'loan-requests', element: <LoanRequests /> },
  { path: 'transactions', element: <Transactions /> },
  // { path: 'transactions/student/:studentId', element: <Transactions /> },
  { path: 'transactions/:studentId', element: <Transactions /> },
  { path: 'financial-simulations', element: <AdminFinancialSimulations /> },
  { path: 'view-financial-simulations/:id', element: <ViewFinancialSimulations /> },

  { path: 'add-scenario', element: <AddScenario /> },
  { path: 'edit-scenario/:scenarioId', element: <EditScenario /> },
  // { path: 'edit-loan/:loanId', element: <EditLoanRequest /> },

  { path: 'payroll', element: <PayrollHub /> },
  { path: 'payroll/import', element: <PayrollImportWizard /> },
  { path: 'payroll/imports/:importId', element: <PayrollImportDetail /> },
  { path: 'payroll/roster', element: <RosterImport /> },
  { path: 'payroll/tax-day', element: <TaxDayExport /> },
  { path: 'cohorts', element: <CohortsHub /> },
  { path: 'cohorts/:cohortId', element: <CohortDetail /> },
  { path: 'roles', element: <Roles /> },
  { path: 'roles/:roleName', element: <RoleDetail /> },
  { path: 'edit-profile', element: <EditProfile /> },
  { path: 'change-password', element: <ChangePassword /> },
  ...teacherRoutes, // Admin has access to all teacher routes
];

const studentRoutes: AppRoute[] = [
  { path: 'dashboard', element: <StudentDashboard /> },
  { path: 'financial-tools', element: <FinancialTools /> },
  { path: 'bills', element: <Bills /> },
  { path: 'loan-requests', element: <StudentLoanRequests /> },
  // { path: 'loan-requests', element: <LoanRequests /> },
  // navigate(`/student/loan-request/${id}?mode=edit`);

  { path: 'new-loan-request', element: <NewLoanRequest /> },
  { path: 'new-loan-request/:id', element: <NewLoanRequest /> },
  { path: 'loan-requests/new', element: <NewLoanRequest /> },
  { path: 'loan-requests/:id', element: <NewLoanRequest /> },
  { path: 'view-loan/:id', element: <NewLoanRequest /> },
  { path: 'view-loan/:loanId', element: <NewLoanRequest /> },

  { path: 'street-cents', element: <StreetCents /> },
  { path: 'street-cents/:id', element: <ViewStreetCent /> },
  { path: 'view-transaction/:id', element: <ViewStreetCent /> },
  { path: 'paychecks', element: <StudentPaychecks /> },
  { path: 'financial-simulations', element: <StudentFinancialSimulations /> },
  { path: 'street-cred', element: <ManageStreetCredsAdmin /> },
  { path: 'view-financial-simulations/:id', element: <StudentViewFinancialSimulations /> },
  { path: 'edit-profile', element: <EditProfile /> },
  { path: 'change-password', element: <ChangePassword /> },
];

export default function Router() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Debug logging for navigation tracking
  useEffect(() => {
    console.log('🧭 Router State Change:', {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: location.state,
      isAuthenticated,
      isLoading,
      userRole: user?.role,
      userId: user?.id,
      timestamp: new Date().toISOString(),
    });
  }, [user, isAuthenticated, isLoading, location]);

  // Memoize routes based on user role
  const routes = useMemo(() => {
    console.log('🏗️ Building Routes:', {
      isLoading,
      isAuthenticated,
      userRole: user?.role,
      currentPath: location.pathname,
      timestamp: new Date().toISOString(),
    });

    // Don't build routes while auth is loading to prevent 404 redirects on page reload
    if (
      (!user || !isAuthenticated) &&
      !['/login', '/forgot-password', '/create-new-password', '/', '/404'].includes(location.pathname)
    ) {
      console.log('⏳ Returning loading routes due to isLoading=true');
      return [
        {
          path: '*',
          element: <LoadingScreen message="Loading routes..." />,
        },
      ];
    }

    const userRole = user?.role?.toUpperCase();
    console.log('✅ Building full routes with userRole:', userRole);

    return [
      {
        path: '/',
        element: <LogoOnlyLayout />,
        children: [
          {
            path: '/',
            element: <RootRedirect isAuthenticated={isAuthenticated} userRole={userRole} />,
          },
          {
            path: 'dashboard',
            element: <RootRedirect isAuthenticated={isAuthenticated} userRole={userRole} replace />,
          },
          {
            path: 'login',
            element: (
              <AuthGuard>
                <Login />
              </AuthGuard>
            ),
          },
          { path: 'forgot-password', element: <ForgotPassword /> },
          { path: 'create-new-password', element: <CreateNewPassword /> },
          { path: '404', element: <NotFound /> },
          { path: '*', element: <Navigate to="/404" /> },
        ],
      },
      {
        path: '/admin',
        element: (
          <ProtectedRoute section="/admin">
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: canAccessRouteSection(userRole, '/admin') ? withGuards('/admin', adminRoutes) : [],
      },
      {
        path: '/teacher',
        element: (
          <ProtectedRoute section="/teacher">
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: canAccessRouteSection(userRole, '/teacher') ? withGuards('/teacher', teacherRoutes) : [],
      },
      {
        path: '/student',
        element: (
          <ProtectedRoute section="/student">
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: canAccessRouteSection(userRole, '/student') ? withGuards('/student', studentRoutes) : [],
      },
      {
        path: '*',
        element: <Navigate to="/404" replace />,
      },
    ];
  }, [user?.role, isAuthenticated, isLoading]);

  // Use the memoized routes - don't return loading here to prevent routing issues
  return useRoutes(routes);
}
